import React, { useMemo, forwardRef, useCallback } from 'react';
import styled from 'styled-components';
import { onlyText } from 'react-children-utilities';
import { ascCtaInteraction, ascMenuInteraction } from 'helpers/src/gtag';
import ChevronRightIcon from 'icons/src/ChevronRight';
import ChevronLeftIcon from 'icons/src/ChevronLeft';
import StyledButton from './styled-button';
import { getVariantStringFromVar } from './get-variant';

const Button = forwardRef(({
  primary,
  secondary,
  outline,
  dark,
  green,
  outlineWhite,
  outlineDark,
  outlineReadable,
  standout,
  link,
  linkHoverUnderline,
  wrapper,
  large,
  wide,
  fullWidth,

  possibleSpan = false,
  prefixIcon: PrefixIcon = null,
  appendIcon: AppendIcon = null,
  chevronLeft = false,
  chevronRight = false,
  className,
  children,
  onClick,
  to,
  href,
  forceLinkOpenInNewTab = false,

  nav = false,
  navType = undefined,
  menuName = undefined,

  // ga tracking properties
  nth,
  eventAction, // for menu
  eventActionResult, // for cta
  elementType,
  ...props
}, ref) => {
  const variant = getVariantStringFromVar({
    primary,
    secondary,
    dark,
    outline,
    green,
    outlineWhite,
    outlineDark,
    outlineReadable,
    standout,
    link,
    wrapper,
  });
  const elementText = onlyText(children) || props['aria-label'];
  const interactionData = useMemo(() => {
    const link_url = to ?? href;
    if (nav) {
      return {
        // page_type,
        element_text: elementText, // whatever is shown on the CTA button / image
        element_subtype: navType ?? 'nav_button', // cta_button, cta_icon, nav_button, nav_icon, nav_tab, hamburger_menu, mega_menu, sidebar_menu, inline_menu, hyperlink, content, share_icon, image, input_field, toggle, dropdown
        element_type: menuName, // The menu tree that this menu item was under,
        // since dealers can duplicate menu choices
        event_action: eventAction ?? (link_url ? 'redirect' : undefined), // click, slide, open, close, exit, increase, decrease, clear, check, uncheck, redirect
      };
    }
    return {
      // page_type: '', // item, itemlist, contact, directions, service, parts, accessories,
      // body_shop, home, build_to_order, custom, complaint, employment, recall, finance,
      // credit, trade, commercial, showroom
      element_text: elementText, // whatever is shown on the CTA button / image
      element_color: variant, // color of CTA button, if available or applicable
      element_order: nth, // represents the order of the cta buttons, if known
      element_type: elementType ?? 'digital_retailing', // header, banner, popup, form, digital_retailing_tool, trade_tool, chat_tool, accessibility_tool, search_tool, scheduling_tool, gallery, map, footer, video, item_details
      event_action_result: eventActionResult ?? (link_url ? 'redirect' : undefined), // click, slide, open, close, exit, increase, decrease, clear, check, uncheck, redirect
      link_url,
    };
  }, [
    nav,
    navType,
    menuName,
    eventAction,
    elementText,
    elementType,
    variant,
    nth,
    to,
    href,
    eventActionResult,
  ]);

  const handleOnClick = useCallback((...args) => {
    if (onClick) {
      onClick(...args);
    }
    const fn = nav ? ascMenuInteraction : ascCtaInteraction;
    fn(interactionData);
  }, [onClick, nav, interactionData]);

  // allow Button to wrap around content that is possibly not a link
  if (possibleSpan && (!to && !href)) {
    return <span>{children}</span>;
  }

  return (
    <StyledButton
      {...props}
      className={className}
      $primary={primary}
      $secondary={secondary}
      $outline={outline}
      $dark={dark}
      $green={green}
      $outlineWhite={outlineWhite}
      $outlineDark={outlineDark}
      $outlineReadable={outlineReadable}
      $standout={standout}
      $link={link}
      $linkHoverUnderline={linkHoverUnderline}
      $wrapper={wrapper}
      $large={large}
      $wide={wide}
      $fullWidth={fullWidth}
      $largerGap={Boolean(PrefixIcon)}
      onClick={handleOnClick}
      to={to}
      href={href}
      ref={ref}
      target={forceLinkOpenInNewTab ? '_blank' : null}
    >
      {Boolean(PrefixIcon) && (
        <PrefixIcon />
      )}
      {chevronLeft && (
        <ChevronLeftIcon />
      )}
      {children}
      {chevronRight && (
        <ChevronRightIcon />
      )}
      {Boolean(AppendIcon) && (
        <AppendIcon />
      )}
    </StyledButton>
  );
});

export default styled(Button)``;
